import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LoadingAnimation } from "@/shared/components/LoadingAnimation";
import { useNotification } from "@/shared/hooks/useNotification";
import {
  tripGuidanceApi,
  type ReconciliationItemDto,
  type CustomerResolutionStatus,
} from "../services/tripGuidanceApi";

// ── Helpers ────────────────────────────────────────────────────────────────

const statusLabel: Record<CustomerResolutionStatus, string> = {
  PendingOtp: "Pending OTP",
  Delivered: "Delivered",
  ProvisionalDelivered: "Provisional",
  PendingReconciliation: "Needs Reconciliation",
  NotDelivered: "Not Delivered",
  CustomerNoShow: "No Show",
  DeliveryFailed: "Delivery Failed",
  ReturnedToDispatch: "Returned to Dispatch",
  ReturnDiscrepancy: "Return Discrepancy",
};

const statusBadgeVariant = (
  s: CustomerResolutionStatus
): "default" | "secondary" | "destructive" | "outline" => {
  if (s === "Delivered") return "default";
  if (s === "NotDelivered" || s === "CustomerNoShow" || s === "DeliveryFailed" || s === "ReturnDiscrepancy") return "destructive";
  if (s === "ReturnedToDispatch") return "default";
  if (s === "PendingReconciliation" || s === "ProvisionalDelivered") return "outline";
  return "secondary";
};

// ── Reconcile Dialog ───────────────────────────────────────────────────────

const ReconcileDialog = ({
  item,
  open,
  onClose,
}: {
  item: ReconciliationItemDto;
  open: boolean;
  onClose: () => void;
}) => {
  const notify = useNotification();
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState("");
  const [decision, setDecision] = useState<"delivered" | "not_delivered" | null>(null);
  const [returnedByLine, setReturnedByLine] = useState<Record<string, number>>(() =>
    Object.fromEntries(item.lines.map((line) => [line.dispatchRecordItemId, line.driverReportedUndeliveredQuantity]))
  );
  const isGoodsReturn = item.lines.length > 0 && !!item.failureKind;

  const mutation = useMutation({
    mutationFn: () => isGoodsReturn
      ? tripGuidanceApi.confirmDeliveryReturn({
          customerStopResolutionId: item.resolutionId,
          lines: item.lines.map((line) => ({
            dispatchRecordItemId: line.dispatchRecordItemId,
            physicallyReturnedQuantity: returnedByLine[line.dispatchRecordItemId] ?? 0,
          })),
          notes: notes.trim() || undefined,
        })
      : tripGuidanceApi.reconcileDelivery({
          resolutionId: item.resolutionId,
          confirmDelivered: decision === "delivered",
          notes: notes.trim() || undefined,
        }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reconciliation-queue"] });
      notify.success("Reconciliation recorded");
      onClose();
    },
    onError: () => notify.error("Failed to save reconciliation"),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isGoodsReturn && decision === null) return;
    mutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Reconcile Delivery</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
            <div>
              <span className="text-muted-foreground">Customer: </span>
              <span className="font-medium">{item.customerName}</span>
            </div>
            <div><span className="text-muted-foreground">Order: </span>{item.orderNumber}</div>
            <div><span className="text-muted-foreground">Trip: </span>{item.dispatchNumber}</div>
            <div><span className="text-muted-foreground">Driver: </span>{item.driverName ?? "—"}</div>
            <div><span className="text-muted-foreground">Vehicle: </span>{item.vehicleNumber ?? "—"}</div>
            <div>
              <span className="text-muted-foreground">Stop: </span>
              {item.stopName}
            </div>
            {!isGoodsReturn && <div>
              <span className="text-muted-foreground">OTP Method: </span>
              {item.otpProvenance ?? "—"}
            </div>}
          </div>

          {isGoodsReturn ? (
            <div className="space-y-3">
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
                <p className="font-medium">{item.failureKind === "NoShow" ? "No Show" : "Delivery Failed"}</p>
                <p>{item.failureReason}</p>
                {item.driverNotes && <p className="text-muted-foreground">{item.driverNotes}</p>}
                <p className="mt-1 font-medium">Driver reports {item.driverReportedUndeliveredQuantity} undelivered</p>
              </div>
              {item.lines.map((line) => {
                const returned = returnedByLine[line.dispatchRecordItemId] ?? 0;
                const variance = line.driverReportedUndeliveredQuantity - returned;
                return (
                  <div key={line.dispatchRecordItemId} className="grid grid-cols-[minmax(0,1fr)_110px] gap-3 rounded-md border p-3">
                    <div>
                      <p className="font-medium">{line.productName}{line.variety ? ` · ${line.variety}` : ""}</p>
                      <p className="text-xs text-muted-foreground">
                        Planned {line.plannedQuantity} · Dispatched {line.quantityDispatched} · Driver reports {line.driverReportedUndeliveredQuantity}
                      </p>
                      <p className={variance > 0 ? "text-xs text-destructive" : "text-xs text-emerald-700"}>
                        Variance: {variance}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <Label>Physically returned</Label>
                      <Input
                        type="number"
                        min={0}
                        max={line.driverReportedUndeliveredQuantity}
                        step="0.01"
                        value={returned}
                        onChange={(event) => setReturnedByLine((current) => ({
                          ...current,
                          [line.dispatchRecordItemId]: Math.min(
                            line.driverReportedUndeliveredQuantity,
                            Math.max(0, Number(event.target.value) || 0)
                          ),
                        }))}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant={decision === "delivered" ? "default" : "outline"}
              onClick={() => setDecision("delivered")}
            >
              ✓ Confirm Delivered
            </Button>
            <Button
              type="button"
              variant={decision === "not_delivered" ? "destructive" : "outline"}
              onClick={() => setDecision("not_delivered")}
            >
              ✗ Not Delivered
            </Button>
          </div>}

          <div className="space-y-1">
            <Label>Reconciliation Notes</Label>
            <Textarea
              placeholder="Optional notes…"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={(!isGoodsReturn && decision === null) || mutation.isPending}>
              {mutation.isPending ? "Saving…" : isGoodsReturn ? "Confirm Physical Return" : "Save Decision"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────

export const ReconciliationPage = () => {
  const [target, setTarget] = useState<ReconciliationItemDto | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["reconciliation-queue"],
    queryFn: () => tripGuidanceApi.getReconciliationQueue(),
  });

  if (isLoading) return <LoadingAnimation />;

  const items = data?.items ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">Delivery Reconciliation</h1>
          <p className="text-sm text-muted-foreground">
            Confirm failed delivery outcomes and the sale goods physically returned to Dispatch
          </p>
        </div>
        <Badge variant="outline">{items.length} pending</Badge>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Pending Reconciliations</CardTitle>
        </CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No items pending reconciliation.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Stop</TableHead>
                  <TableHead>Trip / Order</TableHead>
                  <TableHead>Driver / Vehicle</TableHead>
                  <TableHead>Reported Qty</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Started</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.resolutionId}>
                    <TableCell className="font-medium">{item.customerName}</TableCell>
                    <TableCell>{item.stopName}</TableCell>
                    <TableCell>{item.dispatchNumber}<br/><span className="text-xs text-muted-foreground">{item.orderNumber}</span></TableCell>
                    <TableCell>{item.driverName ?? "—"}<br/><span className="text-xs text-muted-foreground">{item.vehicleNumber ?? "—"}</span></TableCell>
                    <TableCell>{item.driverReportedUndeliveredQuantity || "—"}</TableCell>
                    <TableCell>
                      <Badge variant={statusBadgeVariant(item.status)}>
                        {statusLabel[item.status]}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {item.reconciliationStartedAt
                        ? new Date(item.reconciliationStartedAt).toLocaleDateString("en-KE")
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => setTarget(item)}>
                        Reconcile
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {target && (
        <ReconcileDialog
          item={target}
          open={!!target}
          onClose={() => setTarget(null)}
        />
      )}
    </div>
  );
};
